// VNEC v9 — lib/dashboard.js
// Shared helpers for IFRS & FPA dashboards

import { formatVND } from './forms.js';

// ── Number/money format for dashboard tiles ──
// Compact: 1.5B, 230M, 85K
export function fmtCompact(v) {
  const n = parseFloat(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return sign + (abs / 1e9).toFixed(abs >= 1e10 ? 1 : 2) + 'B';
  if (abs >= 1e6) return sign + (abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e3) return sign + (abs / 1e3).toFixed(0) + 'K';
  return sign + abs.toFixed(0);
}

// Full VND with thousand separators
export function fmtVND(v) { return formatVND(v || 0); }

// Percentage with 1 decimal
export function fmtPct(v) {
  const n = parseFloat(v) || 0;
  return n.toFixed(1) + '%';
}

// ── Color palette for charts (Gold/Navy theme extended) ──
export const PALETTE = {
  navy:        '#000066',
  navyLight:   '#4A4A8F',
  gold:        '#FFCC00',
  goldDark:    '#B88700',
  green:       '#1B6B3A',
  greenLight:  '#8DC39B',
  red:         '#A8000A',
  redLight:    '#D98A8F',
  orange:      '#D97706',
  orangeLight: '#F4A261',
  blue:        '#185FA5',
  blueLight:   '#7FB3D9',
  purple:      '#534AB7',
  gray:        '#6B7280',
  grayLight:   '#D1D5DB'
};

// 10-color series palette for charts
export const SERIES_COLORS = [
  PALETTE.navy, PALETTE.gold, PALETTE.green, PALETTE.orange, PALETTE.blue,
  PALETTE.purple, PALETTE.red, PALETTE.greenLight, PALETTE.goldDark, PALETTE.blueLight
];

// ── Variance color badge ──
// Returns { color, bg, label } based on pct (actual/budget * 100)
export function variancePct(pct) {
  if (pct <= 80) return { color: PALETTE.green,  bg: '#E7F5EC', label: 'Tốt' };
  if (pct <= 100) return { color: PALETTE.goldDark, bg: '#FAEEDA', label: 'Theo dõi' };
  if (pct <= 110) return { color: PALETTE.orange, bg: '#FCECD6', label: 'Cảnh báo' };
  return { color: PALETTE.red, bg: '#FCEBEB', label: 'Vượt' };
}

// ── CPI/SPI traffic light ──
export function cpiStatus(cpi) {
  if (cpi >= 1) return { color: PALETTE.green, label: 'Đang tiết kiệm' };
  if (cpi >= 0.95) return { color: PALETTE.goldDark, label: 'Bám sát' };
  if (cpi >= 0.9) return { color: PALETTE.orange, label: 'Cảnh báo' };
  return { color: PALETTE.red, label: 'Vượt chi phí' };
}

// ── Chart.js defaults (call once on app init) ──
export function setupChartDefaults() {
  if (typeof Chart === 'undefined') return;
  Chart.defaults.font.family = 'Cambria, Georgia, serif';
  Chart.defaults.font.size = 12;
  Chart.defaults.color = '#374151';
  Chart.defaults.plugins.legend.labels.font = { size: 11, family: 'Cambria' };
  Chart.defaults.plugins.tooltip.titleFont = { family: 'Cambria', size: 12, weight: 'bold' };
  Chart.defaults.plugins.tooltip.bodyFont = { family: 'Cambria', size: 11 };
}

// ── Render a Chart.js chart, destroying previous instance if exists ──
const _chartInstances = new Map();
export function renderChart(canvasId, config) {
  if (typeof Chart === 'undefined') {
    console.warn('[dashboard] Chart.js not loaded');
    return null;
  }
  const canvas = document.getElementById(canvasId);
  if (!canvas) return null;
  if (_chartInstances.has(canvasId)) {
    _chartInstances.get(canvasId).destroy();
  }
  const ch = new Chart(canvas, config);
  _chartInstances.set(canvasId, ch);
  return ch;
}

export function destroyChart(canvasId) {
  if (_chartInstances.has(canvasId)) {
    _chartInstances.get(canvasId).destroy();
    _chartInstances.delete(canvasId);
  }
}

export function destroyAllCharts() {
  _chartInstances.forEach(c => c.destroy());
  _chartInstances.clear();
}

// ── Month label formatter: "2026-03" → "T3/26" ──
export function fmtMonth(ym) {
  if (!ym || ym.length < 7) return ym || '';
  const y = ym.substring(2, 4);
  const m = parseInt(ym.substring(5, 7), 10);
  return 'T' + m + '/' + y;
}

// ── KPI tile HTML ──
export function kpiTile({ label, value, hint, color = 'navy', money = false }) {
  const colorClass = { green: 'g', red: 'r', gold: 'au', orange: 'o', blue: 'b' }[color] || '';
  const display = money ? fmtCompact(value) + ' VNĐ' : value;
  return `
    <div class="kpi-tile ${colorClass}">
      <div class="kpi-lbl">${label}</div>
      <div class="kpi-val">${display}</div>
      ${hint ? `<div class="kpi-hint">${hint}</div>` : ''}
    </div>`;
}
